'use client';
import XClose from '@unifyapps/icons/outline/XClose';
import { clsx } from 'clsx';
import { forwardRef, memo, useMemo } from 'react';
import { slowCn } from '../../lib/utils';
import { isNative } from '../../utils/isNative';
import { Box } from '../Box';
import { IconButton } from '../IconButton';
import { Tooltip } from '../Tooltip';
import { Typography as BaseTypography } from '../Typography';
import { TypographyWithTooltip } from '../Typography/TypographyWithTooltip';
import { decoratorVariants, dotVariants, labelVariants, pillVariants } from './styles';
import { getNativeLineHeight } from './styles/native/nativeLineHeight';
import type { PillProps } from './types';

function Dot({ color, variant, dotClassName, statusDotStyle }: PillProps) {
  return (
    <Box
      className={dotVariants({ color, variant, className: dotClassName })}
      style={statusDotStyle}
    />
  );
}

const getLabelVariant = (_size: 'md' | 'sm' | 'xs' | 'xxs' | undefined) => {
  switch (_size) {
    case 'md':
      return 'text-sm';
    case 'sm':
      return 'text-xs';
    case 'xs':
    case 'xxs':
      return 'text-xxs';
    default:
      return 'text-xxxs';
  }
};

const Pill = forwardRef<HTMLDivElement, PillProps>(function Pill(props, ref) {
  const {
    className,
    startDecoratorComponent: StartDecoratorComponent,
    endDecoratorComponent: EndDecoratorComponent,
    startDecoratorNode,
    endDecoratorNode,
    variant = 'clear',
    shape = 'square',
    color = 'gray',
    label,
    isDot,
    statusDot,
    dismissible,
    onDismiss,
    tooltip,
    pillClassName,
    labelStyle,
    decoratorStyle,
    labelClassName,
    labelTypography,
    disabled,
    size = 'sm',
    statusDotStyle,
    dotClassName,
    enableTooltip = true,
    ...rest
  } = props;

  const pillClassNames = pillVariants({ variant, color, className, shape, disabled, size });
  const labelClassNames = labelVariants({ variant, color });
  const decoratorClassNames = decoratorVariants({ color, size, variant });

  const startDecorator = StartDecoratorComponent ? (
    <StartDecoratorComponent
      className={decoratorClassNames}
      data-slot="start-decorator"
      style={decoratorStyle}
    />
  ) : (
    startDecoratorNode
  );

  const endDecorator = EndDecoratorComponent ? (
    <EndDecoratorComponent
      className={decoratorClassNames}
      data-slot="end-decorator"
      style={decoratorStyle}
    />
  ) : (
    endDecoratorNode
  );

  const dataAttrs = useMemo(
    () => ({
      'data-color': color,
      'data-variant': variant,
      'data-size': size,
      'data-shape': shape,
      // Pill renders as a non-form <div>, so :disabled pseudo-class will not match.
      // Emit aria-disabled so theme-manager selectors like [aria-disabled='true'] can target the disabled state.
      ...(disabled ? { 'aria-disabled': true } : {}),
    }),
    [color, variant, size, shape, disabled],
  );
  const statusDotNode = statusDot ? (
    <Dot
      dotClassName={clsx(
        'bg-current shrink-0 aspect-square',
        size === 'md' ? '!size-[8px]' : '!size-[6px]',
        dotClassName,
      )}
      statusDotStyle={statusDotStyle}
    />
  ) : null;

  const dismissibleNode = dismissible ? (
    <IconButton
      className="!p-0 !min-h-0 !min-w-0 !size-auto"
      disabled={disabled}
      Icon={XClose}
      iconClassName={decoratorClassNames}
      onClick={(e) => {
        e.stopPropagation();
        onDismiss?.(e);
      }}
      size="xs"
      style={decoratorStyle}
      variant="ghost"
    />
  ) : null;

  if (!label) {
    return (
      <Box
        className={clsx(pillClassNames, labelClassNames, size !== 'md' ? '!p-sm' : '')}
        ref={ref}
        {...dataAttrs}
        {...rest}
      >
        {statusDotNode}
        {isDot ? <Dot {...props} /> : startDecorator}
        {dismissibleNode}
      </Box>
    );
  }

  const nativeLineHeight = isNative()
    ? getNativeLineHeight(labelTypography?.variant ?? getLabelVariant(size))
    : '';

  const Typography = enableTooltip ? TypographyWithTooltip : BaseTypography;

  const content = (
    <Box
      className={slowCn(
        pillClassNames,
        labelClassNames,
        { 'cursor-pointer': Boolean(props.onClick) },
        { 'gap-x-xs': isDot || statusDot || dismissible },
        { 'gap-x-xs': Boolean(endDecorator) },
        { 'gap-x-xs ps-sm': Boolean(startDecorator) },
        pillClassName,
      )}
      ref={ref}
      {...dataAttrs}
      {...rest}
    >
      {statusDotNode}
      {isDot ? <Dot {...props} /> : startDecorator}
      <Typography
        as="p"
        className={clsx(labelClassNames, labelClassName, nativeLineHeight)} // Hardcoded line height required for native platforms - default line height doesn't center text properly in React Native
        fontFace={labelTypography?.fontFace}
        maxNumberOfLines={labelTypography?.maxNumberOfLines ?? 1}
        style={labelStyle}
        tooltipClassName="!break-words"
        variant={labelTypography?.variant ?? getLabelVariant(size)}
        weight={labelTypography?.weight ?? 'medium'}
      >
        {label}
      </Typography>
      {endDecorator}
      {dismissibleNode}
    </Box>
  );

  return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
});

Pill.displayName = 'Pill';

const MemoizedPill = memo(Pill) as typeof Pill;
export default MemoizedPill;
